import { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Grid,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  AssignResidentRequest,
  AssignResidentResponse,
  GetUsersParams,
  UserRole,
  ResidentDetailed,
  useAssignResidentMutation,
  useAssignResidentExternalPaymentMutation,
  useGetCommunityUsersQuery,
  useBlockUserMutation,
  useUnblockUserMutation,
  useLazyExportUsersToXlsxQuery,
  useGetUserByIdQuery,
  useGetResidentPeriodsQuery,
  adminCommunitiesApi,
} from "../../../entities/admin-communities";
import {
  useGetClubsQuery,
  useSetResidentClubsMutation,
} from "../../../entities/admin-clubs";
import { useAppDispatch } from "../../../app/store";
import { useGetMyPermissionsQuery } from "../../../entities/me";
import GroupsIcon from "@mui/icons-material/Groups";
import {
  ADMIN_PERMISSION_USER_PERMISSIONS,
  ADMIN_PERMISSION_BLOCK_UNBLOCK_USERS,
} from "../../../shared/constants/admin-permission-ids";
import { format } from "date-fns";
import { LoadingIndicator } from "../../../shared/ui/loading-indicator";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import WorkIcon from "@mui/icons-material/Work";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TelegramIcon from "@mui/icons-material/Telegram";
import LanguageIcon from "@mui/icons-material/Language";
import LinkIcon from "@mui/icons-material/Link";
import { ResidentPeriodsView } from "./resident-periods-view";

const CommunityUsersManagement = () => {
  const dispatch = useAppDispatch();
  // States
  const [search, setSearch] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState<UserRole | "">("");
  const [blockedFilter, setBlockedFilter] = useState<boolean | undefined>(
    undefined,
  );
  const [sort, setSort] = useState<"firstname" | "lastname" | "createdat">(
    "lastname",
  );
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [limit] = useState(25);

  // Detail dialog states
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedDetailUserId, setSelectedDetailUserId] = useState<
    string | null
  >(null);
  const [detailTabValue, setDetailTabValue] = useState(0);

  // Block/Unblock modal states
  const [openBlockDialog, setOpenBlockDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [isBlocked, setIsBlocked] = useState(false);

  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUserId, setMenuUserId] = useState<string | null>(null);

  // Assign resident dialog states
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [assignData, setAssignData] = useState<AssignResidentRequest>({
    beginAt: "",
    endAt: "",
    description: "",
  });
  const [assignMode, setAssignMode] = useState<"noPayment" | "externalPayment">(
    "noPayment",
  );

  // Error handling states
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);

  // Clubs assignment dialog states
  const [openClubsDialog, setOpenClubsDialog] = useState(false);
  const [clubsDialogUserId, setClubsDialogUserId] = useState<string | null>(
    null,
  );
  const [selectedClubIds, setSelectedClubIds] = useState<number[]>([]);

  // Create params object for the query
  const params: GetUsersParams = {
    sort,
    order,
    limit,
  };

  if (search) params.search = search;
  if (phoneNumber) params.phoneNumber = phoneNumber;
  if (role) params.role = role as UserRole;
  if (blockedFilter !== undefined) params.blocked = blockedFilter;
  if (pageToken) params.pageToken = pageToken;

  // RTK Query
  const { data: usersData, isLoading } = useGetCommunityUsersQuery(params);
  const { data: permissionsData } = useGetMyPermissionsQuery();

  // Selected user detailed info and periods (only when exResident)
  const { data: residentDetail, isLoading: isLoadingResidentDetail } =
    useGetUserByIdQuery(selectedDetailUserId || "", {
      skip: !selectedDetailUserId || !openDetailDialog,
    });
  const residentContainer =
    (residentDetail as unknown as {
      resident?: ResidentDetailed;
      profile?: ResidentDetailed;
    }) || {};
  const residentEntity: ResidentDetailed | undefined =
    residentContainer.resident ?? residentContainer.profile;
  const isExResident = Boolean(residentEntity?.exResident);
  const { data: residentPeriods, isFetching: isFetchingPeriods } =
    useGetResidentPeriodsQuery(selectedDetailUserId || "", {
      skip: !selectedDetailUserId || !openDetailDialog || !isExResident,
    });

  const canBlockUnblockUsers =
    permissionsData?.permissions?.includes(
      ADMIN_PERMISSION_BLOCK_UNBLOCK_USERS,
    ) ?? false;
  // UserPermissions (permission ID 2): назначение резидентом
  const canAssignResidents =
    permissionsData?.permissions?.includes(ADMIN_PERMISSION_USER_PERMISSIONS) ??
    false;

  // Block/Unblock mutations
  const [blockUser, { isLoading: isBlocking }] = useBlockUserMutation();
  const [unblockUser, { isLoading: isUnblocking }] = useUnblockUserMutation();
  const [assignResident] = useAssignResidentMutation();
  const [assignResidentExternalPayment] =
    useAssignResidentExternalPaymentMutation();

  // Export users to Excel
  const [exportUsersToXlsx, { isLoading: isExporting }] =
    useLazyExportUsersToXlsxQuery();

  // Clubs catalog and assignment
  const { data: clubsData } = useGetClubsQuery();
  const [setResidentClubs, { isLoading: isSavingClubs }] =
    useSetResidentClubsMutation();

  // Reset pagination when filters change
  useEffect(() => {
    setPageToken(undefined);
  }, [search, phoneNumber, role, blockedFilter, sort, order]);

  // Handle next page
  const handleNextPage = () => {
    const next = usersData?.pageToken?.trim();
    if (next) {
      setPageToken(next);
    }
  };

  // Handle previous page
  const handlePreviousPage = () => {
    setPageToken(undefined);
  };

  // Handle opening user details
  const handleOpenDetails = (userId: string) => {
    setSelectedDetailUserId(userId);
    setOpenDetailDialog(true);
    setDetailTabValue(0);
  };

  // Handle closing user details
  const handleCloseDetails = () => {
    setOpenDetailDialog(false);
    setSelectedDetailUserId(null);
  };

  // Handle opening clubs assignment dialog
  const handleOpenClubsDialog = (resident: ResidentDetailed) => {
    setClubsDialogUserId(resident.id);
    setSelectedClubIds(resident.clubs?.map((c) => c.id) ?? []);
    setOpenClubsDialog(true);
  };

  const handleCloseClubsDialog = () => {
    setOpenClubsDialog(false);
    setClubsDialogUserId(null);
    setSelectedClubIds([]);
  };

  const handleToggleClub = (clubId: number) => {
    setSelectedClubIds((prev) =>
      prev.includes(clubId)
        ? prev.filter((id) => id !== clubId)
        : [...prev, clubId],
    );
  };

  const handleSaveClubs = async () => {
    if (!clubsDialogUserId) return;
    try {
      await setResidentClubs({
        id: clubsDialogUserId,
        body: { clubIds: selectedClubIds },
      }).unwrap();
      dispatch(
        adminCommunitiesApi.util.invalidateTags([
          { type: "Residents", id: clubsDialogUserId },
          "Residents",
          "CommunityUsers",
        ]),
      );
      handleCloseClubsDialog();
    } catch (error) {
      console.error("Error assigning clubs:", error);
      setErrorMessage("Произошла ошибка при назначении клубов");
      setOpenErrorSnackbar(true);
    }
  };

  // Handle block/unblock user dialog
  const handleOpenBlockDialog = (
    userId: string,
    userName: string,
    blocked: boolean,
  ) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setIsBlocked(blocked);
    setOpenBlockDialog(true);
  };

  const handleCloseBlockDialog = () => {
    setOpenBlockDialog(false);
    setSelectedUserId(null);
    setSelectedUserName("");
    setIsBlocked(false);
  };

  // Handle block/unblock user
  const handleBlockUnblockUser = async () => {
    if (!selectedUserId) return;

    try {
      if (isBlocked) {
        await unblockUser(selectedUserId).unwrap();
      } else {
        await blockUser(selectedUserId).unwrap();
      }
      handleCloseBlockDialog();
    } catch (error) {
      console.error("Error blocking/unblocking user:", error);
    }
  };

  // Handle menu actions
  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    userId: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuUserId(userId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuUserId(null);
  };

  const handleMenuAction = (action: string) => {
    if (!menuUserId) return;

    const user = usersData?.users?.find((u) => u.id === menuUserId);
    if (!user) return;

    switch (action) {
      case "block":
      case "unblock":
        handleOpenBlockDialog(
          user.id,
          `${user.firstName} ${user.lastName}`,
          user.blocked,
        );
        break;
      case "assign":
        setSelectedUserId(user.id);
        setAssignData({ beginAt: "", endAt: "", description: "" });
        setAssignMode("noPayment");
        setOpenAssignDialog(true);
        break;
    }
    handleCloseMenu();
  };

  const closeAssignResidentDialog = () => {
    setOpenAssignDialog(false);
    setAssignData({ beginAt: "", endAt: "", description: "" });
    setAssignMode("noPayment");
    setSelectedUserId(null);
  };

  const getAssignResidentErrorMessage = (error: unknown): string => {
    if (
      error &&
      typeof error === "object" &&
      "data" in error &&
      error.data &&
      typeof error.data === "object" &&
      "message" in error.data &&
      typeof error.data.message === "string" &&
      error.data.message.trim()
    ) {
      return error.data.message;
    }

    return "Произошла ошибка при назначении резидентом";
  };

  // Handle assign resident
  const handleAssignResident = async () => {
    if (!selectedUserId) return;

    const trimmedNote = assignData.description?.trim();
    const payload: AssignResidentRequest = {
      beginAt: assignData.beginAt,
      endAt: assignData.endAt,
      ...(trimmedNote ? { description: trimmedNote } : {}),
    };

    const assignMutation =
      assignMode === "externalPayment"
        ? assignResidentExternalPayment
        : assignResident;

    try {
      const response: AssignResidentResponse = await assignMutation({
        id: selectedUserId,
        data: payload,
      }).unwrap();

      if (
        response?.code === "applicationNotApproved" ||
        response?.result === false
      ) {
        setErrorMessage(
          response?.message ||
            "Нельзя назначить резидентом: заявка на участие не одобрена",
        );
        setOpenErrorSnackbar(true);
        return;
      }

      closeAssignResidentDialog();
    } catch (error: unknown) {
      console.error("Error assigning resident:", error);
      setErrorMessage(getAssignResidentErrorMessage(error));
      setOpenErrorSnackbar(true);
    }
  };

  // Handle export to Excel
  const handleExportToXlsx = async () => {
    try {
      const result = await exportUsersToXlsx().unwrap();

      // Create download link
      const url = window.URL.createObjectURL(result);
      const link = document.createElement("a");
      link.href = url;
      link.download = `community-users-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting users:", error);
      setErrorMessage("Произошла ошибка при экспорте пользователей");
      setOpenErrorSnackbar(true);
    }
  };

  // Render role name in Russian
  const renderRoleName = (userRole: UserRole) => {
    switch (userRole) {
      case "guest":
        return "Гость";
      case "user":
        return "Пользователь";
      case "resident":
        return "Резидент";
      default:
        return userRole;
    }
  };

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography fontWeight={600} fontSize={24}>
          Управление пользователями сообщества
        </Typography>

        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportToXlsx}
          disabled={isExporting}
          sx={{ ml: 2 }}
        >
          {isExporting ? "Экспорт..." : "Экспорт в Excel"}
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <TextField
            label="Поиск по имени или фамилии"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: "200px" }}
          />
          <TextField
            label="Номер телефона"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: "200px" }}
          />
          <FormControl size="small" sx={{ minWidth: "150px" }}>
            <InputLabel>Роль</InputLabel>
            <Select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole | "")}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="guest">Гость</MenuItem>
              <MenuItem value="user">Пользователь</MenuItem>
              <MenuItem value="resident">Резидент</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: "150px" }}>
            <InputLabel>Статус</InputLabel>
            <Select
              value={
                blockedFilter === undefined ? "" : blockedFilter.toString()
              }
              onChange={(e) => {
                const value = e.target.value;
                setBlockedFilter(value === "" ? undefined : value === "true");
              }}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="false">Не заблокированные</MenuItem>
              <MenuItem value="true">Заблокированные</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: "150px" }}>
            <InputLabel>Сортировка</InputLabel>
            <Select
              value={sort}
              onChange={(e) =>
                setSort(
                  e.target.value as "firstname" | "lastname" | "createdat",
                )
              }
            >
              <MenuItem value="firstname">По имени</MenuItem>
              <MenuItem value="lastname">По фамилии</MenuItem>
              <MenuItem value="createdat">По дате регистрации</MenuItem>
            </Select>
            <FormHelperText>
              Порядок:{" "}
              <Button
                size="small"
                onClick={() => setOrder(order === "asc" ? "desc" : "asc")}
              >
                {order === "asc" ? "По возрастанию" : "По убыванию"}
              </Button>
            </FormHelperText>
          </FormControl>
        </Box>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "secondary.main" }}>
            <TableRow>
              <TableCell>Фото</TableCell>
              <TableCell>Имя</TableCell>
              <TableCell>Телефон</TableCell>
              <TableCell>Роль</TableCell>
              <TableCell>Дата регистрации</TableCell>
              <TableCell>Статус</TableCell>
              {(canBlockUnblockUsers || canAssignResidents) && (
                <TableCell>Действия</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {usersData?.users?.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Avatar src={user.avatarUrl} alt={user.firstName}></Avatar>
                </TableCell>
                <TableCell>{user.firstName + " " + user.lastName}</TableCell>
                <TableCell>{user.phoneNumber}</TableCell>
                <TableCell>
                  <Chip
                    label={renderRoleName(user.role)}
                    color={
                      user.role === "guest"
                        ? "default"
                        : user.role === "user"
                          ? "primary"
                          : "secondary"
                    }
                  />
                </TableCell>
                <TableCell>{format(user.createdAt, "dd.MM.yyyy")}</TableCell>
                <TableCell>
                  <Chip
                    label={user.blocked ? "Заблокирован" : "Активен"}
                    color={user.blocked ? "error" : "success"}
                    icon={user.blocked ? <BlockIcon /> : <CheckCircleIcon />}
                    sx={{
                      "& .MuiChip-icon": {
                        color: "#fff",
                        fontSize: "16px",
                      },
                    }}
                  />
                </TableCell>
                {(canBlockUnblockUsers || canAssignResidents) && (
                  <TableCell>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      sx={{ minWidth: "auto", p: 1, mr: 1 }}
                      onClick={() => handleOpenDetails(user.id)}
                    >
                      <PersonIcon fontSize="small" sx={{ color: "#fff" }} />
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      sx={{ minWidth: "auto", p: 1 }}
                      onClick={(e) => handleOpenMenu(e, user.id)}
                    >
                      <EditIcon fontSize="small" sx={{ color: "#fff" }} />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" mt={2}>
        <Button
          disabled={!pageToken}
          onClick={handlePreviousPage}
          variant="outlined"
          sx={{ mr: 1 }}
        >
          Предыдущая
        </Button>
        <Button
          disabled={!usersData?.pageToken?.trim()}
          onClick={handleNextPage}
          variant="outlined"
        >
          Следующая
        </Button>
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 },
        }}
      >
        {menuUserId && (
          <MenuItem
            onClick={() =>
              handleMenuAction(
                usersData?.users?.find((u) => u.id === menuUserId)?.blocked
                  ? "unblock"
                  : "block",
              )
            }
          >
            <ListItemIcon>
              {usersData?.users?.find((u) => u.id === menuUserId)?.blocked ? (
                <CheckCircleIcon fontSize="small" />
              ) : (
                <BlockIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                usersData?.users?.find((u) => u.id === menuUserId)?.blocked
                  ? "Разблокировать"
                  : "Заблокировать"
              }
            />
          </MenuItem>
        )}
        {menuUserId && canAssignResidents && (
          <MenuItem onClick={() => handleMenuAction("assign")}>
            <ListItemIcon>
              <WorkIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Назначить резидентом" />
          </MenuItem>
        )}
      </Menu>

      {/* Assign Resident Dialog */}
      <Dialog
        open={openAssignDialog}
        onClose={closeAssignResidentDialog}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 2,
          }}
        >
          <Typography>Назначить резидентом</Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 3, minWidth: 400 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Тип назначения
          </Typography>
          <ToggleButtonGroup
            value={assignMode}
            exclusive
            fullWidth
            onChange={(_, value) => {
              if (value !== null) {
                setAssignMode(value);
              }
            }}
            sx={{ mb: 3 }}
            color="primary"
          >
            <ToggleButton value="noPayment">Без оплаты</ToggleButton>
            <ToggleButton value="externalPayment">Внешняя оплата</ToggleButton>
          </ToggleButtonGroup>
          {assignMode === "externalPayment" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Учитываются бонусы и прочие правила как при оплате подпиской
              (например, бонусные дни).
            </Alert>
          )}
          <Grid container spacing={3}>
            <Grid sx={{ xs: 12, p: 1 }}>
              <TextField
                fullWidth
                label="Дата начала"
                type="datetime-local"
                value={assignData.beginAt}
                onChange={(e) =>
                  setAssignData({ ...assignData, beginAt: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid sx={{ xs: 12, p: 1 }}>
              <TextField
                fullWidth
                label="Дата окончания"
                type="datetime-local"
                value={assignData.endAt}
                onChange={(e) =>
                  setAssignData({ ...assignData, endAt: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <TextField
              fullWidth
              label="Примечание"
              placeholder="Необязательно"
              value={assignData.description ?? ""}
              onChange={(e) =>
                setAssignData({ ...assignData, description: e.target.value })
              }
              multiline
              minRows={3}
            />
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={closeAssignResidentDialog} color="inherit">
            Отмена
          </Button>
          <Button
            onClick={handleAssignResident}
            variant="contained"
            color="primary"
          >
            Назначить
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 0,
          }}
        >
          <Tabs
            value={detailTabValue}
            onChange={(_, newValue) => setDetailTabValue(newValue)}
            variant="fullWidth"
          >
            <Tab label="Информация" />
            {isExResident && <Tab label="Периоды резиденства" />}
          </Tabs>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {detailTabValue === 0 &&
            (isLoadingResidentDetail ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : residentEntity ? (
              <ResidentDetailView
                isFetching={false}
                resident={residentEntity}
                onEditClubs={handleOpenClubsDialog}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Нет данных для отображения
              </Typography>
            ))}
          {detailTabValue === 1 && isExResident && (
            <ResidentPeriodsView
              isFetching={isFetchingPeriods}
              periods={residentPeriods?.periods}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{ borderTop: "1px solid", borderColor: "divider", py: 2 }}
        >
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            color="secondary"
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block/Unblock Confirmation Dialog */}

      <Dialog
        open={openBlockDialog}
        onClose={handleCloseBlockDialog}
        aria-labelledby="block-dialog-title"
        aria-describedby="block-dialog-description"
      >
        <DialogTitle id="block-dialog-title">
          {isBlocked
            ? "Разблокировать пользователя?"
            : "Заблокировать пользователя?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {isBlocked
              ? `Вы уверены, что хотите разблокировать пользователя "${selectedUserName}"?`
              : `Вы уверены, что хотите заблокировать пользователя "${selectedUserName}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBlockDialog} color="primary">
            Отмена
          </Button>
          <Button
            onClick={handleBlockUnblockUser}
            color={isBlocked ? "success" : "error"}
            variant="contained"
            disabled={isBlocking || isUnblocking}
          >
            {isBlocking || isUnblocking ? (
              <CircularProgress size={20} />
            ) : isBlocked ? (
              "Разблокировать"
            ) : (
              "Заблокировать"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clubs Assignment Dialog */}
      <Dialog
        open={openClubsDialog}
        onClose={handleCloseClubsDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{ borderBottom: "1px solid", borderColor: "divider", py: 2 }}
        >
          Клубы коллаборации
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          {clubsData?.clubs?.length ? (
            clubsData.clubs.map((club) => (
              <FormControlLabel
                key={club.id}
                control={
                  <Checkbox
                    checked={selectedClubIds.includes(club.id)}
                    onChange={() => handleToggleClub(club.id)}
                  />
                }
                label={club.name}
                sx={{ display: "flex", width: "100%" }}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              Справочник клубов пуст
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={handleCloseClubsDialog} color="inherit">
            Отмена
          </Button>
          <Button
            onClick={handleSaveClubs}
            variant="contained"
            color="primary"
            disabled={isSavingClubs}
          >
            {isSavingClubs ? <CircularProgress size={20} /> : "Сохранить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenErrorSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenErrorSnackbar(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunityUsersManagement;

// Component for displaying resident-like details for a user
const ResidentDetailView = ({
  isFetching,
  resident,
  onEditClubs,
}: {
  isFetching: boolean;
  resident: ResidentDetailed;
  onEditClubs: (resident: ResidentDetailed) => void;
}) => {
  if (isFetching) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Grid container columnSpacing={2} rowSpacing={0}>
      <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 0 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ width: "100%", minWidth: 0 }}
        >
          <Avatar
            src={resident.avatarUrl}
            alt={resident.firstName}
            sx={{
              width: 180,
              height: 180,
              mb: 2,
              border: "3px solid",
              borderColor: "primary.main",
            }}
          >
            {resident.firstName?.charAt(0)}
            {resident.lastName?.charAt(0)}
          </Avatar>
          <Typography variant="h5" gutterBottom>
            {resident.firstName} {resident.lastName}
          </Typography>
          {resident.additionalStatus && (
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              {resident.additionalStatus}
            </Typography>
          )}
          {resident.caliber && (
            <Chip
              label={
                resident.caliber === "voshod"
                  ? "Восходящая звезда"
                  : resident.caliber === "siyay"
                    ? "Сияющая звезда"
                    : resident.caliber === "sover"
                      ? "Совершенная звезда"
                      : resident.caliber
              }
              color={
                resident.caliber === "voshod"
                  ? "default"
                  : resident.caliber === "siyay"
                    ? "primary"
                    : "secondary"
              }
              sx={{ mt: 1, mb: 2 }}
            />
          )}
          <Chip
            label={resident.blocked ? "Заблокирован" : "Активен"}
            color={resident.blocked ? "error" : "success"}
            sx={{ mt: 1, mb: 2, ml: 1 }}
          />
          <Paper
            elevation={1}
            sx={{ p: 3, width: "100%", maxWidth: "100%", borderRadius: 2, overflow: "hidden" }}
          >
            {resident.phoneNumber && (
              <Typography
                variant="body1"
                sx={{ display: "flex", alignItems: "flex-start", mb: 1, minWidth: 0 }}
              >
                <PhoneIcon fontSize="small" sx={{ mr: 1, mt: 0.25, flexShrink: 0 }} />
                {resident.phoneNumber}
              </Typography>
            )}
            {resident.email && (
              <Typography
                variant="body1"
                sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}
              >
                <EmailIcon fontSize="small" sx={{ mr: 1, mt: 0.25, flexShrink: 0 }} />
                <Box
                  component="span"
                  sx={{ minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}
                >
                  {resident.email}
                </Box>
              </Typography>
            )}
          </Paper>
        </Box>
      </Grid>
      <Grid
        size={{ xs: 12, md: 8 }}
        sx={{ display: "flex", flexDirection: "column", gap: "5px" }}
      >
        {resident.about && (
          <Box sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <PersonIcon sx={{ mr: 1 }} />О пользователе
            </Typography>
            <Typography variant="body1">{resident.about}</Typography>
          </Box>
        )}
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            Компетенции
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {resident.competencies?.length ? (
              resident.competencies.map((comp) => (
                <Chip
                  key={comp.id}
                  label={comp.name}
                  color="primary"
                  variant="outlined"
                />
              ))
            ) : (
              <Typography variant="body1">Нет компетенций</Typography>
            )}
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            Компании
          </Typography>
          {resident.companies?.length ? (
            resident.companies.map((company) => (
              <Paper
                key={company.id}
                sx={{
                  p: 2,
                  mb: "5px",
                  "&:last-of-type": { mb: 0 },
                  bgcolor: "secondary.light",
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  {company.name}
                </Typography>
                {company.description && (
                  <Typography variant="body2" sx={{ my: 1 }}>
                    {company.description}
                  </Typography>
                )}
                {company.employeeRole && (
                  <Typography
                    variant="body2"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    Роль: {company.employeeRole}
                  </Typography>
                )}
              </Paper>
            ))
          ) : (
            <Typography variant="body1">Нет компаний</Typography>
          )}
        </Paper>

        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <GroupsIcon sx={{ mr: 1 }} />
              Клубы коллаборации
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => onEditClubs(resident)}
            >
              Изменить
            </Button>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {resident.clubs?.length ? (
              resident.clubs.map((club) => (
                <Chip
                  key={club.id}
                  avatar={<Avatar src={club.logoUrl}>{club.name?.[0]}</Avatar>}
                  label={club.name}
                  variant="outlined"
                />
              ))
            ) : (
              <Typography variant="body1">Нет клубов</Typography>
            )}
          </Box>
        </Paper>

        {resident.hobbies && resident.hobbies.length > 0 && (
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              Хобби
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {resident.hobbies.map((hobby) => (
                <Chip key={hobby.id} label={hobby.name} variant="outlined" />
              ))}
            </Box>
          </Paper>
        )}

        {resident.socials && resident.socials.length > 0 && (
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <LinkIcon sx={{ mr: 1 }} />
              Социальные сети
            </Typography>
            <List dense>
              {resident.socials.map((social, index: number) => (
                <ListItem key={index} sx={{ px: 1 }}>
                  <ListItemIcon>
                    {social.type === "facebook" && <FacebookIcon />}
                    {social.type === "instagram" && <InstagramIcon />}
                    {social.type === "linkedin" && <LinkedInIcon />}
                    {social.type === "vk" && (
                      <img src="/vk-icon.svg" width="24" />
                    )}
                    {social.type === "telegram" && <TelegramIcon />}
                    {![
                      "facebook",
                      "instagram",
                      "linkedin",
                      "vk",
                      "telegram",
                    ].includes(social.type) && <LanguageIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      social.type?.charAt(0).toUpperCase() +
                      social.type?.slice(1)
                    }
                    secondary={
                      <Link
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary.main"
                      >
                        {social.url}
                      </Link>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {resident.activity && (
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              Активность
            </Typography>
            <Grid container spacing={2}>
              <Grid sx={{ xs: 6, sm: 3 }}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "primary.dark",
                    color: "white",
                    borderRadius: 2,
                    transition: "transform 0.2s",
                    "&:hover": { transform: "scale(1.05)" },
                  }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {resident.activity.participantCount}
                  </Typography>
                  <Typography variant="body2">Участие в событиях</Typography>
                </Paper>
              </Grid>
              <Grid sx={{ xs: 6, sm: 3 }}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "secondary.dark",
                    color: "white",
                    borderRadius: 2,
                    transition: "transform 0.2s",
                    "&:hover": { transform: "scale(1.05)" },
                  }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {resident.activity.requestCount}
                  </Typography>
                  <Typography variant="body2">Заявки</Typography>
                </Paper>
              </Grid>
              <Grid sx={{ xs: 6, sm: 3 }}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "info.dark",
                    color: "white",
                    borderRadius: 2,
                    transition: "transform 0.2s",
                    "&:hover": { transform: "scale(1.05)" },
                  }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {resident.activity.commentCount}
                  </Typography>
                  <Typography variant="body2">Комментарии</Typography>
                </Paper>
              </Grid>
              <Grid sx={{ xs: 6, sm: 3 }}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "success.dark",
                    color: "white",
                    borderRadius: 2,
                    transition: "transform 0.2s",
                    "&:hover": { transform: "scale(1.05)" },
                  }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {resident.activity.invitationCount}
                  </Typography>
                  <Typography variant="body2">Приглашения</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};
